// Adapted from https://github.com/GeorgeGally/creative_coding/blob/b0e53e65bd289a91f4d0e88e1279310a79a0e3ff/js/mic.js
export class MicrophoneAnalyzer {
    private FFT_SIZE: number;
    private SAMPLE_RATE: number;
    private vol = 0;
    // Experimentation show 70 as a decent representation for regular speech
    private peak_volume = 70;
    // Experimentation show 30 as a decent representation for microphone activity, ignoring static noise.
    private activityLimit = 30;
    private spectrum: Uint8Array<ArrayBuffer>;

    private input: AudioNode | null;
    private context: AudioContext;
    private analyser: AnalyserNode;

    private lastZeroVolume: Date | null = null;
    private notifiedThatZeroWasPersistent = false;
    // The lower bound for the notification boundary. A positive non-zero number would disregard a certain amount of background noise.
    public zeroVolumeNotificationBoundaryVolume = 0;
    // The time in milliseconds that is the boundary between setting volume to zero and notifying about it.
    public zeroVolumeNotificationBoundaryTime = 1000;
    public onZeroVolumeChanged: null | ((isZeroVolume: boolean) => void);
    public onVolumeActivityChanged: null | ((volume: number) => void);

    constructor(stream: MediaStream, _fft = 2048) {
        this.FFT_SIZE = _fft || 2048;

        this.spectrum = new Uint8Array(this.FFT_SIZE / 2);

        const AC: typeof AudioContext =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.AudioContext ?? (<any>window).webkitAudioContext;
        const context = new AC();
        this.context = context;
        this.SAMPLE_RATE = context.sampleRate;

        // analyser extracts frequency, waveform, and other data
        const analyser = context.createAnalyser();
        this.analyser = analyser;
        analyser.smoothingTimeConstant = 0.2;
        analyser.fftSize = this.FFT_SIZE;

        const node = context.createScriptProcessor(this.FFT_SIZE * 2, 1, 1);

        node.onaudioprocess = () => {
            // getByteFrequencyData mutates the input with the amplitude for each frequency
            analyser.getByteFrequencyData(this.spectrum);

            this.vol = this.getRMS(this.spectrum);

            if (this.onZeroVolumeChanged) {
                if (this.vol <= this.zeroVolumeNotificationBoundaryVolume) {
                    if (this.lastZeroVolume == null) {
                        this.notifiedThatZeroWasPersistent = false;
                        this.lastZeroVolume = new Date();
                    } else if (!this.notifiedThatZeroWasPersistent) {
                        const now = Date.now();
                        const wentZero = this.lastZeroVolume.getTime();
                        const diff = now - wentZero;
                        if (diff > this.zeroVolumeNotificationBoundaryTime) {
                            this.notifiedThatZeroWasPersistent = true;
                            this.onZeroVolumeChanged(true);
                        }
                    }
                } else if (this.lastZeroVolume != null) {
                    if (this.notifiedThatZeroWasPersistent) {
                        this.onZeroVolumeChanged?.(false);
                    }
                    this.lastZeroVolume = null;
                }
            }

            // update peak
            if (this.vol > this.peak_volume) {
                this.peak_volume = this.vol;
            }

            if (this.vol > this.activityLimit) {
                if (this.onVolumeActivityChanged) {
                    this.onVolumeActivityChanged(this.vol);
                }
            }
        };

        analyser.connect(node);
        node.connect(context.destination);

        this.setStream(stream);
    }

    public setStream(stream: MediaStream): void {
        if (stream.getAudioTracks().length === 0) {
            throw new Error('MediaStream must have at least 1 AudioTrack');
        }

        this.input?.disconnect();
        this.input = null;

        const input = this.context.createMediaStreamSource(stream);
        input.connect(this.analyser);
        this.input = input;
    }

    public close(): void {
        this.onZeroVolumeChanged = null;
        this.onVolumeActivityChanged = null;
        this.input?.disconnect();
        this.input = null;
        this.context.close();
        this.analyser.disconnect();
    }

    getVol(): number {
        // map total volume to percent for convenience
        return (this.vol / this.peak_volume) * 100;
    }

    getVolume(): number {
        return this.getVol();
    }

    //A more accurate way to get overall volume
    getRMS(spectrum: Uint8Array | number[]): number {
        let rms = 0;
        for (let i = 0; i < spectrum.length; i++) {
            rms += spectrum[i] * spectrum[i];
        }
        rms /= spectrum.length;
        rms = Math.sqrt(rms);
        return rms;
    }
}
